import { Knex } from "knex";
import moment from "moment-timezone";

export const getNextWorkingDay = async (
  db: Knex,
  today: string,
  holidayDates?: Set<string>,
) => {
  let nextDay = moment(today).add(1, "day").format("YYYY-MM-DD");

  while (true) {
    const isHoliday = holidayDates
      ? holidayDates.has(nextDay)
      : !!(await db("nse_holidays")
          .whereRaw('DATE("Date") = ?', [nextDay])
          .first());

    if (!isHoliday) {
      // nextDay is not a holiday -> return it
      return nextDay;
    }

    // If holiday, check next day
    nextDay = moment(nextDay).add(1, "day").format("YYYY-MM-DD");
  }
};

export const getPreviousWorkingDay = async (db: Knex, today: string) => {
  let previousDay = moment(today).subtract(1, "day").format("YYYY-MM-DD");

  while (true) {
    // Check if nextDay is in holidays
    const holiday = await db("nse_holidays")
      .whereRaw('DATE("Date") = ?', [previousDay])
      .first();

    if (!holiday) {
      // nextDay is not a holiday -> return it
      return previousDay;
    }

    // If holiday, check next day
    previousDay = moment(previousDay).subtract(1, "day").format("YYYY-MM-DD");
  }
};

export const getHotMonths = (referenceDate: moment.Moment) => {
  const monthsToCheck: string[] = [];
  for (let i = 0; i < 2; i++) {
    const monthName = referenceDate.clone().add(i, "months").format("MMM");
    monthsToCheck.push(monthName);
  }
  return monthsToCheck;
};

export async function getHotSectors(
  db: Knex,
  usersPlan: string,
  referenceDate: moment.Moment,
  months_count: number = 2,
  derivatives: boolean = true,
): Promise<Record<string, Set<string>>[]> {
  const monthsToCheck: number[] = [];
  for (let i = 0; i < months_count; i++) {
    monthsToCheck.push(referenceDate.clone().add(i, "months").month() + 1);
  }
  const whereConditions: string[] = [
    "(filter.sector IS NOT NULL AND filter.industry IS NOT NULL)",
    "(filter.sector != '' AND filter.industry != '')",
    `filter.month IN (${monthsToCheck.join(", ")})`,
  ];
  if (derivatives) {
    whereConditions.push("filter.indicesname = 'Derivatives'");
  }
  const sectorStatsQuery = `
    WITH user_plan as (
      SELECT * from public.abillion_userplans
      where plan_name = '${usersPlan}' AND status = 'Active' AND country = 'India'
    ),
    timeseriesfilter AS (
      SELECT
        ss.date,
        ss.symbol,
        ss.growth,
        EXTRACT(YEAR FROM ss.date) AS year,
        EXTRACT(MONTH FROM ss.date) AS month,
        DENSE_RANK() OVER (
          PARTITION BY ss.symbol
          ORDER BY EXTRACT(YEAR FROM ss.date) DESC
        ) AS past_year,
        indx.indicesname,
        inf.sector,
        inf.industry,
        seg.sebicat,
        CASE
          WHEN inf.market_cap IS NULL THEN 'NA'
          WHEN inf.market_cap >= 500000000000 THEN 'Large Cap'
          WHEN inf.market_cap >= 150000000000 AND inf.market_cap < 500000000000 THEN 'Mid Cap'
          WHEN inf.market_cap < 20000000000 THEN 'Micro Cap'
          ELSE 'Small Cap'
        END AS marketcap_cat
      FROM sourcedata_seasonal ss
      LEFT JOIN public.stock_indexcategory indx ON ss.symbol = indx."Symbol"
      LEFT JOIN public.company_info_nse inf ON ss.symbol = inf.symbol
      LEFT JOIN segment_sebi seg ON ss.symbol = seg.symbol::text AND seg.nsesymbol IS NOT NULL
      WHERE EXTRACT(YEAR FROM ss.date) >= EXTRACT(YEAR FROM CURRENT_DATE) - 15
    ),
    filtered AS (
      SELECT
        filter.month,
        filter.sector,
        filter.industry,
        filter.symbol,
        COUNT(*) AS instances,
        SUM(CASE WHEN filter.growth > 0 THEN 1 ELSE 0 END) AS upside,
        ROUND(AVG(filter.growth)::numeric, 4) AS growthavg
      FROM timeseriesfilter filter
      WHERE
        ${whereConditions.join(" AND ")}
      GROUP BY filter.month, filter.sector, filter.industry, filter.symbol
    ),
    sector_stats AS (
      SELECT
        f.month,
        f.sector,
        ROUND(AVG(f.growthavg) * 100, 2) AS sector_growth_avg,
        ROUND(AVG(CASE WHEN f.instances = 0 THEN 0 ELSE (f.upside::numeric / f.instances) END) * 100, 2) AS ratio
      FROM filtered f
      GROUP BY f.month, f.sector
    )
    SELECT
    month,
    sector,
    ratio
    FROM sector_stats
    ORDER BY ratio DESC;
  `;

  const sectorResults = await db.raw(sectorStatsQuery);
  const rows = sectorResults.rows;

  const topSectorsPerMonth: Record<string, Set<string>>[] = [];

  monthsToCheck.forEach((m) => {
    const monthRows = rows.filter(
      (r: { month: number }) => Number(r.month) === m,
    );
    const topCount = Math.ceil(monthRows.length / 3);
    const topForMonth = monthRows.slice(0, topCount);
    const monthSectors = new Set<string>(
      topForMonth.map((r: { sector: string }) => r.sector),
    );
    topSectorsPerMonth.push({ [m]: monthSectors });
  });
  return topSectorsPerMonth;
}

export const getStockMovement = async (
  db: Knex,
  usersPlan: string,
  referenceDate: moment.Moment,
  symbols: string[],
) => {
  const monthsToCheck: number[] = [];
  for (let i = 0; i < 2; i++) {
    monthsToCheck.push(referenceDate.clone().add(i, "months").month() + 1);
  }
  const currentYear = new Date().getFullYear();
  const startYear = currentYear - 15;
  const stockMovementQuery = `
    WITH user_plan as (
      SELECT * from public.abillion_userplans
      where plan_name = '${usersPlan}' AND status = 'Active' AND country = 'India'
    ),
    timeseriesfilter AS (
      SELECT
        ss.date,
        ss.symbol,
        ss.growth,
        EXTRACT(YEAR FROM ss.date) AS year,
        EXTRACT(MONTH FROM ss.date) AS month,
        DENSE_RANK() OVER (
          PARTITION BY ss.symbol
          ORDER BY EXTRACT(YEAR FROM ss.date) DESC
        ) AS past_year,
        indx.indicesname,
        inf.sector,
        inf.industry,
        seg.sebicat,
        CASE
        WHEN inf.market_cap IS NULL THEN 'NA'
        WHEN inf.market_cap >= 500000000000 THEN 'Large Cap'
        WHEN inf.market_cap >= 150000000000 AND inf.market_cap < 500000000000 THEN 'Mid Cap'
        WHEN inf.market_cap < 20000000000 THEN 'Micro Cap'
        ELSE 'Small Cap'
        END AS marketcap_cat
      FROM sourcedata_seasonal ss
      JOIN user_plan up on ss.symbol = up.symbol
      LEFT JOIN public.stock_indexcategory indx ON ss.symbol = indx."Symbol"
      LEFT JOIN public.company_info_nse inf ON ss.symbol = inf.symbol
      LEFT JOIN segment_sebi seg ON ss.symbol = seg.symbol::text AND seg.nsesymbol IS NOT NULL
      WHERE EXTRACT(YEAR FROM ss.date) >= ${startYear}
    ),
    filtered AS (
      SELECT * FROM timeseriesfilter filter
      WHERE filter.symbol IN ('${symbols.join("','")}') AND month IN (${monthsToCheck.join(",")})
    )
    SELECT
      f.symbol,
      f.month,
      TO_CHAR(TO_DATE(f.month::text, 'MM'), 'FMMonth') AS month_name,
      ROUND(AVG(f.growth)::numeric, 4)*100 AS average_growth
    FROM filtered f
    GROUP BY f.symbol, f.month
    ORDER BY f.symbol, f.month;
  `;
  const sectorResults = await db.raw(stockMovementQuery);
  const rows = sectorResults.rows;
  return rows;
};
